import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Download,
  Copy,
  RefreshCcw,
  Trash2,
  MessageCircle,
  Mail,
  Loader2,
  Building2,
  Pencil,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import ReportView from "../../components/ReportView";
import {
  api,
  errMsg,
  downloadPdf,
  reportLink,
  whatsappLink,
} from "../../lib/api";
import { useBranding } from "../../lib/branding";

export default function AdminInspectionDetail() {
  const { iid } = useParams();
  const nav = useNavigate();
  const [ins, setIns] = useState(null);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState("");
  const [builders, setBuilders] = useState(null);
  const branding = useBranding();
  useEffect(() => {
    api
      .get(`/inspections/${iid}`)
      .then(({ data }) => setIns(data))
      .catch((e) => toast.error(errMsg(e)));
    api
      .get(`/inspections/${iid}/share`)
      .then(({ data }) => setShare(data))
      .catch(() => {});
  }, [iid]);
  if (!ins)
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  const run = async (k, fn) => {
    setBusy(k);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const link = share ? reportLink(iid, share.token) : "";
  const done = ins.status === "completed";
  const btn =
    "tap flex h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold";
  const openBuilders = async () =>
    setBuilders(
      (await api.get("/users", { params: { role: "builder" } })).data,
    );
  const shareTo = (bid) =>
    run("builder", async () => {
      const { data } = await api.post(`/inspections/${iid}/share-builder`, {
        builder_id: bid,
      });
      setIns((x) => ({ ...x, builder_share: data.builder_share }));
      setBuilders(null);
      toast.success(
        bid
          ? `Shared with ${data.builder_share.builder_name}`
          : "Builder access removed",
      );
    });
  return (
    <>
      <Dialog open={!!builders} onOpenChange={(v) => !v && setBuilders(null)}>
        <DialogContent data-testid="share-builder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Share defect list with builder
            </DialogTitle>
            <DialogDescription>
              The builder sees property, defects, photos and recommendations —
              never the customer's name, phone or email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {builders?.map((b) => (
              <button
                key={b.user_id}
                onClick={() => shareTo(b.user_id)}
                className={`tap w-full rounded-md border p-3 text-left ${ins.builder_share?.builder_id === b.user_id ? "border-teal-600 bg-teal-50" : "border-slate-200 hover:border-slate-900"}`}
                data-testid={`share-builder-${b.user_id}`}
              >
                <div className="font-semibold">{b.builder_name || b.name}</div>
                <div className="text-xs text-slate-500">
                  {b.name} · {b.email}
                </div>
              </button>
            ))}
            {builders?.length === 0 && (
              <p className="text-sm text-slate-500">
                No builder accounts yet. Add one under People & Builders.
              </p>
            )}
            {ins.builder_share && (
              <button
                onClick={() => shareTo(null)}
                className="tap h-10 w-full rounded-md border border-rose-200 text-sm font-semibold text-rose-600"
                data-testid="unshare-builder-button"
              >
                Remove builder access
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ReportView
        ins={ins}
        actions={
          <>
            <button
              onClick={() => run("pdf", () => downloadPdf(iid))}
              className={`${btn} bg-teal-500 text-slate-950`}
              data-testid="admin-download-pdf-button"
            >
              {busy === "pdf" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              PDF
            </button>
            {done && (
              <button
                onClick={() => nav(`/admin/inspections/${iid}/edit-report`)}
                className={`${btn} border border-teal-500 text-teal-200`}
                data-testid="admin-edit-report-button"
              >
                <Pencil className="h-4 w-4" />
                Edit report
              </button>
            )}
            {done && share && (
              <>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(link);
                    toast.success("Customer link copied");
                  }}
                  className={`${btn} border border-slate-700 text-white`}
                  data-testid="admin-copy-link-button"
                >
                  <Copy className="h-4 w-4" />
                  Copy link
                </button>
                <a
                  href={whatsappLink(
                    share.customer,
                    iid,
                    link,
                    branding?.company,
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className={`${btn} bg-[#25D366] text-white`}
                  data-testid="admin-whatsapp-button"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
                <button
                  onClick={() =>
                    run("mail", async () => {
                      const { data } = await api.post(
                        `/inspections/${iid}/email`,
                      );
                      toast.success(`Emailed ${data.to}`);
                    })
                  }
                  className={`${btn} border border-slate-700 text-white`}
                  data-testid="admin-email-button"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </button>
                <button
                  onClick={() =>
                    run("re", async () => {
                      const { data } = await api.post(
                        `/inspections/${iid}/reinspection`,
                        {},
                      );
                      toast.success(
                        `Re-inspection ${data.inspection_id} created`,
                      );
                      nav(`/admin/inspections/${data.inspection_id}`);
                    })
                  }
                  className={`${btn} border border-slate-700 text-white`}
                  data-testid="admin-create-reinspection-button"
                >
                  <RefreshCcw className="h-4 w-4" />
                  Re-inspection
                </button>
              </>
            )}
            {done && (
              <button
                onClick={openBuilders}
                className={`${btn} border border-slate-700 text-white`}
                data-testid="admin-share-builder-button"
              >
                <Building2 className="h-4 w-4" />
                {ins.builder_share
                  ? `Builder: ${ins.builder_share.builder_name}`
                  : "Share with builder"}
              </button>
            )}
            <button
              onClick={() =>
                window.confirm(`Delete ${iid} and its photos permanently?`) &&
                run("del", async () => {
                  await api.delete(`/inspections/${iid}`);
                  toast.success("Deleted");
                  nav("/admin/inspections");
                })
              }
              className={`${btn} ml-auto border border-rose-800 text-rose-300`}
              data-testid="admin-delete-inspection-button"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </>
        }
      />
    </>
  );
}
